import React, { useState, useEffect } from "react";
import { Head, Link, router } from "@inertiajs/react";
import Layout from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { MessageCircle, User, Calendar, ChevronRight, Filter, X, ChevronUp, ChevronDown } from "lucide-react";

interface Discussion {
  id: number;
  title: string;
  content: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
  offer: {
    id: number;
    name: string;
    client: string;
    provider: string;
  };
  comments_count: number;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
  prev_page: number | null;
  next_page: number | null;
}

interface FilterOption {
  id: number;
  name: string;
}

interface Filters {
  client_id?: string;
  provider_id?: string;
  offer_id?: string;
}

interface FilterOptions {
  clients: FilterOption[];
  providers: FilterOption[];
  offers: FilterOption[];
}

interface IndexProps {
  discussions: Discussion[];
  pagination: Pagination;
  filters: Filters;
  filter_options: FilterOptions;
}

function Index({ discussions, pagination, filters, filter_options }: IndexProps) {
  const [selectedClientId, setSelectedClientId] = useState<string>(filters.client_id || "");
  const [selectedProviderId, setSelectedProviderId] = useState<string>(filters.provider_id || "");
  const [selectedOfferId, setSelectedOfferId] = useState<string>(filters.offer_id || "");
  const [isFiltering, setIsFiltering] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleClientChange = (value: string) => {
    setSelectedClientId(value);
    setSelectedProviderId("");
    setSelectedOfferId("");
    
    // Fetch updated providers and offers for this client
    applyFilters({ client_id: value });
  };

  const handleProviderChange = (value: string) => {
    setSelectedProviderId(value);
    setSelectedOfferId("");
    
    // Fetch updated offers for this client and provider
    applyFilters({ 
      client_id: selectedClientId, 
      provider_id: value 
    });
  };

  const handleOfferChange = (value: string) => {
    setSelectedOfferId(value);
    
    // Apply all filters
    applyFilters({ 
      client_id: selectedClientId, 
      provider_id: selectedProviderId,
      offer_id: value
    });
  };

  const applyFilters = (newFilters: Filters) => {
    setIsFiltering(true);
    
    const params = new URLSearchParams();
    if (newFilters.client_id) params.append('client_id', newFilters.client_id);
    if (newFilters.provider_id) params.append('provider_id', newFilters.provider_id);
    if (newFilters.offer_id) params.append('offer_id', newFilters.offer_id);
    
    router.get(`/discussions?${params.toString()}`, {}, {
      preserveState: false,
      onFinish: () => setIsFiltering(false),
    });
  };

  const clearFilters = () => {
    setSelectedClientId("");
    setSelectedProviderId("");
    setSelectedOfferId("");
    router.get('/discussions', {}, { preserveState: false });
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', page.toString());
    router.get(`/discussions?${params.toString()}`, {}, { preserveState: false });
  };

  const hasActiveFilters = selectedClientId || selectedProviderId || selectedOfferId;

  return (
    <Layout>
      <Head title="Discussions | Mailkit - Resolute" />
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
              All Discussions
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Browse discussions from all offers
            </p>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-xl border border-gray-200 mb-6">
            <div className="px-4 py-3">
              <button 
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center">
                  <Filter className="w-4 h-4 mr-2 text-gray-500" />
                  <h3 className="text-base font-semibold text-gray-900">Filters</h3>
                  {hasActiveFilters && (
                    <span className="ml-2 text-xs text-gray-500">
                      ({[selectedClientId, selectedProviderId, selectedOfferId].filter(Boolean).length} active)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {hasActiveFilters && filtersExpanded && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilters();
                      }}
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all duration-200"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                  {filtersExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </div>
              </button>
            </div>
            
            {filtersExpanded && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Client Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                    <Select
                      value={selectedClientId}
                      onValueChange={handleClientChange}
                      disabled={isFiltering}
                    >
                      <SelectTrigger className="w-full" size="sm">
                        <SelectValue placeholder="All Clients" />
                      </SelectTrigger>
                      <SelectContent>
                        {filter_options.clients.map((client) => (
                          <SelectItem key={client.id} value={client.id.toString()}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Provider Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                    <Select
                      value={selectedProviderId}
                      onValueChange={handleProviderChange}
                      disabled={!selectedClientId || isFiltering || filter_options.providers.length === 0}
                    >
                      <SelectTrigger className="w-full" size="sm">
                        <SelectValue placeholder={selectedClientId ? "All Providers" : "Select client first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {filter_options.providers.map((provider) => (
                          <SelectItem key={provider.id} value={provider.id.toString()}>
                            {provider.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Offer Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Offer</label>
                    <Select
                      value={selectedOfferId}
                      onValueChange={handleOfferChange}
                      disabled={!selectedProviderId || isFiltering || filter_options.offers.length === 0}
                    >
                      <SelectTrigger className="w-full" size="sm">
                        <SelectValue placeholder={selectedProviderId ? "All Offers" : "Select provider first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {filter_options.offers.map((offer) => (
                          <SelectItem key={offer.id} value={offer.id.toString()}>
                            {offer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Discussions List */}
          <Card className="shadow-none">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Topic
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Offer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Author
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Comments
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {discussions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <MessageCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                            <h3 className="text-sm font-medium text-gray-900">No discussions found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {hasActiveFilters 
                                ? "Try adjusting your filters to see more discussions."
                                : "No discussions have been created yet."}
                            </p>
                            {hasActiveFilters && (
                              <div className="mt-4">
                                <Button
                                  variant="outline"
                                  onClick={clearFilters}
                                  size="sm"
                                >
                                  Clear Filters
                                </Button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      discussions.map((discussion) => (
                        <tr
                          key={discussion.id}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              href={`/discussions/${discussion.id}?from=discussions`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800"
                            >
                              {discussion.title}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              href={`/offers/${discussion.offer.id}`}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              {discussion.offer.name}
                            </Link>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {discussion.offer.client}
                              </span>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {discussion.offer.provider}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{discussion.user.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <MessageCircle className="w-4 h-4 mr-1 text-gray-400" />
                              {discussion.comments_count}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(discussion.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {(pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of{" "}
                    {pagination.total_count} discussions
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={!pagination.prev_page}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-700">
                      Page {pagination.current_page} of {pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={!pagination.next_page}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
        </div>
      </div>
    </Layout>
  );
}

export default Index;